import { runInAction } from "mobx";

import { storageService } from "../storage";

import type { Account } from "@aptos-labs/ts-sdk";
import { isValidAddress } from "@utils/Address";
import { connectionFlowService } from "./ConnectionFlowService";
import { subAccountService } from "./SubAccountService";

import type { SubAccount, Wallet } from "./types";

export interface AccountLoaderCallbacks {
  setSubAccount: (account: Account | null) => void;
  setAllSubAccounts: (address: string, accounts: SubAccount[]) => void;
  getAllSubAccounts: () => Map<string, SubAccount[]>;
  setSubAccountsLoaded: (value: boolean) => void;
  setIsLoadingSubAccount: (value: boolean) => void;
  getIsLoadingSubAccount: () => boolean;
  setActiveTradingAccountAddress: (address: string) => void;
  getActiveTradingAccountAddress: () => string | null;
  generateRootAccountToken: () => Promise<void>;
  fetchAllPortfolios: () => Promise<void>;
}

export class AccountLoader {
  loadSubAccountsFromSession = async (
    callbacks: Pick<AccountLoaderCallbacks, "getAllSubAccounts" | "setSubAccountsLoaded">,
  ): Promise<void> => {
    try {
      const stored = await storageService.getSubAccounts();
      const allSubAccounts = callbacks.getAllSubAccounts();

      runInAction(() => {
        allSubAccounts.clear();
        stored.forEach((accounts, address) => allSubAccounts.set(address, accounts));
        callbacks.setSubAccountsLoaded(true);
      });
    } catch (error) {
      console.error("Error loading sub accounts from session:", error);
      runInAction(() => {
        callbacks.setSubAccountsLoaded(true);
      });
    }
  };

  loadSubAccount = async (address: string, callbacks: AccountLoaderCallbacks, forceRefresh = false): Promise<void> => {
    if (!isValidAddress(address)) return;
    if (callbacks.getIsLoadingSubAccount() && !forceRefresh) return;
    callbacks.setIsLoadingSubAccount(true);

    try {
      const storedFromStorage = await storageService.getSubAccounts();
      const currentStored = storedFromStorage.get(address) || [];
      const subAccounts = await subAccountService.loadSubAccountsWithBlockchainData(
        address,
        currentStored,
        forceRefresh,
      );

      if (!subAccounts.length) {
        callbacks.setIsLoadingSubAccount(false);
        return;
      }

      callbacks.setAllSubAccounts(address, subAccounts);

      const allSubAccounts = callbacks.getAllSubAccounts();
      await storageService.setSubAccounts(allSubAccounts);

      connectionFlowService.initializeSubAccountFromStorage(subAccounts, {
        setSubAccount: callbacks.setSubAccount,
      });

      if (!callbacks.getActiveTradingAccountAddress()) {
        const savedAddress = await storageService.getActiveTradingAddress();
        const tradingAccounts = subAccounts.filter((acc) => acc.type === "trading" && acc.privateKey);
        const selected = connectionFlowService.selectInitialTradingAccount(tradingAccounts, savedAddress);
        if (selected) {
          callbacks.setActiveTradingAccountAddress(selected.address);
        }
      }

      await callbacks.generateRootAccountToken();
      await callbacks.fetchAllPortfolios().catch(() => {});
    } finally {
      callbacks.setIsLoadingSubAccount(false);
    }
  };

  findAndRestoreTradingAccount = async (
    wallet: Wallet,
    targetAddress: string,
    expectedNonce: string,
    allSubAccounts: Map<string, SubAccount[]>,
  ): Promise<SubAccount | null> => {
    if (!wallet) return null;

    const candidate = await subAccountService.generateTradingAccountByNonceFromMaster(wallet, expectedNonce);
    if (!candidate || candidate.address !== targetAddress) return null;

    const rootAddr = wallet.account!.address.toString();
    const existing = allSubAccounts.get(rootAddr) ?? [];
    const idx = existing.findIndex((acc) => acc.address === targetAddress);

    const updatedAccount: SubAccount =
      idx >= 0
        ? { ...existing[idx], privateKey: candidate.privateKey, publicKey: candidate.publicKey, nonce: candidate.nonce }
        : candidate;

    const updatedAccounts =
      idx >= 0 ? [...existing.slice(0, idx), updatedAccount, ...existing.slice(idx + 1)] : [...existing, candidate];

    allSubAccounts.set(rootAddr, updatedAccounts);
    await storageService.setSubAccounts(allSubAccounts);

    return updatedAccount;
  };

  updateNoncesFromBlockchain = async (
    address: string,
    allSubAccounts: Map<string, SubAccount[]>,
    setAllSubAccounts: (address: string, accounts: SubAccount[]) => void,
  ): Promise<void> => {
    const existingAccounts = allSubAccounts.get(address);
    if (!existingAccounts?.length) return;

    try {
      const updated = await subAccountService.loadSubAccountsWithBlockchainData(address, existingAccounts, true);
      setAllSubAccounts(address, updated);
      await storageService.setSubAccounts(allSubAccounts);
    } catch {
      // Silent - nonce update is non-critical
    }
  };
}

export const accountLoader = new AccountLoader();
